/**
 * ч.1040: чек-ап після міграції — чисті помічники сторінки і секції майстра
 * (без React), щоб перевіряти тестом без DOM.
 */
import type {
  CheckupFinding, CheckupReport, CheckupSeverity, CheckupSummary,
} from '@/api/migrationRuns';

export type { CheckupFinding, CheckupReport, CheckupSeverity, CheckupSummary };

export const SEVERITY_LABEL: Record<CheckupSeverity, string> = {
  ERROR: 'помилка обліку',
  WARN: 'потребує рішення',
  INFO: 'зверніть увагу',
};

/** Токени бейджа за серйозністю (Закон дизайну #11 — лише змінні теми). */
export function severityTokens(sev: CheckupSeverity): { fg: string; bg: string; bd: string } {
  const k = sev === 'ERROR' ? 'red' : sev === 'WARN' ? 'amber' : 'blue';
  return { fg: `var(--badge-${k}-fg)`, bg: `var(--badge-${k}-bg)`, bd: `var(--badge-${k}-bd)` };
}

/** Порядок показу: помилки → рішення → увага; всередині — за кодом. */
export function sortFindings(findings: CheckupFinding[]): CheckupFinding[] {
  const order: Record<CheckupSeverity, number> = { ERROR: 0, WARN: 1, INFO: 2 };
  return [...findings].sort((a, b) =>
    order[a.severity] - order[b.severity] || a.code.localeCompare(b.code));
}

/** Один рядок підсумку: «2 помилки · 3 потребують рішення · 5 зверніть увагу». */
export function summaryLine(s: CheckupSummary): string {
  if (s.total === 0) return 'зауважень немає';
  const parts: string[] = [];
  if (s.error) parts.push(`${s.error} ${plural(s.error, 'помилка', 'помилки', 'помилок')}`);
  if (s.warn) parts.push(`${s.warn} ${plural(s.warn, 'потребує', 'потребують', 'потребують')} рішення`);
  if (s.info) parts.push(`${s.info} — зверніть увагу`);
  if (s.rules_failed) parts.push(`${s.rules_failed} не виконано`);
  return parts.join(' · ');
}

/** Правила, які пройшли без знахідок (для блоку «перевірено, все гаразд»). */
export function passedRules(report: CheckupReport): CheckupReport['rules'] {
  const hit = new Set(report.findings.map((f) => f.code));
  return report.rules.filter((r) => !hit.has(r.code));
}

/** Значення комірки деталей: масив → через кому, число як є, інше — рядком. */
export function cellText(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v.filter((x) => x != null).map(String).join(', ');
  if (typeof v === 'boolean') return v ? 'так' : 'ні';
  return String(v);
}

function plural(n: number, one: string, few: string, many: string): string {
  const n10 = n % 10; const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}
